import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, X } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'

// Nav items — add/remove entries here and make sure each key exists in
// `src/i18n/routes.ts` and has a `nav.*` translation in the locales files.
const navItems: { key: RouteKey; labelKey: string }[] = [
  { key: 'home', labelKey: 'nav.home' },
  { key: 'company', labelKey: 'nav.company' },
  { key: 'services', labelKey: 'nav.services' },
  { key: 'history', labelKey: 'nav.history' },
  { key: 'certifications', labelKey: 'nav.certifications' },
  { key: 'contacts', labelKey: 'nav.contacts' },
]

export function Header() {
  const { t } = useTranslation()
  const location = useLocation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)

  // Current slug (second path segment after the language prefix)
  const pathSegments = location.pathname.split('/').filter(Boolean)
  const currentSlug = pathSegments[1] || ''

  // Pages with a light hero that need the header to be solid immediately
  const pagesWithLightTop: string[] = []
  const forceSolid = pagesWithLightTop.includes(currentSlug)

  // Detect scroll
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileOpen(false)
  }, [location.pathname])

  // Lock body scroll while mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileOpen])

  const isTransparent = !isScrolled && !forceSolid

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isTransparent ? 'bg-transparent' : 'bg-white/95 backdrop-blur-md shadow-md'
      )}
    >
      <nav
        aria-label={t('aria.mainNavigation', 'Main navigation')}
        className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8 h-16 md:h-20"
      >
        {/* Logo — replace with <img src="..." /> in your project */}
        <LocalizedLink to="home" className="flex-shrink-0">
          <img
            src={
              isTransparent
                ? '/images/logos/chiggiato-logo-reverse.png'
                : '/images/logos/chiggiato-logo.png'
            }
            alt="Chiggiato SpA"
            className="h-10 md:h-12 object-contain transition-opacity"
          />
        </LocalizedLink>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-1">
          {navItems.map(({ key, labelKey }) => {
            const routePath = localizedRoutes[key]
            const isActive =
              routePath.it === currentSlug ||
              routePath.en === currentSlug ||
              (key === 'home' && currentSlug === '')

            return (
              <LocalizedLink
                key={key}
                to={key}
                className={cn(
                  'group relative px-3 py-2 text-sm font-medium rounded-md transition-colors',
                  isTransparent
                    ? 'text-white/90 hover:text-white hover:bg-white/10'
                    : 'text-gray-900 hover:bg-gray-100',
                  isActive && 'font-semibold'
                )}
              >
                {t(labelKey)}
                {/* Active indicator */}
                <span
                  className={cn(
                    'absolute bottom-0 left-3 right-3 h-0.5 rounded-full transition-all',
                    isActive
                      ? 'scale-x-100 opacity-100'
                      : 'scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-100',
                    isTransparent ? 'bg-white' : 'bg-gray-900'
                  )}
                />
              </LocalizedLink>
            )
          })}
        </div>

        {/* Desktop Actions */}
        <div className="hidden lg:flex items-center gap-2">
          <LanguageSelector variant="desktop" isScrolled={!isTransparent} />
        </div>

        {/* Mobile Toggle */}
        <button
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className={cn(
            'lg:hidden p-2 rounded-md transition-colors',
            isTransparent
              ? 'text-white hover:bg-white/10'
              : 'text-gray-900 hover:bg-gray-100'
          )}
          aria-label={isMobileOpen ? t('aria.closeMenu') : t('aria.openMenu')}
          aria-expanded={isMobileOpen}
        >
          {isMobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Mobile Navigation Overlay */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden fixed inset-0 top-16 md:top-20 bg-white z-40 overflow-y-auto"
          >
            <nav
              className="flex flex-col py-4"
              aria-label={t('aria.mobileNavigation', 'Mobile navigation')}
            >
              {navItems.map(({ key, labelKey }) => {
                const routePath = localizedRoutes[key]
                const isActive =
                  routePath.it === currentSlug ||
                  routePath.en === currentSlug ||
                  (key === 'home' && currentSlug === '')

                return (
                  <LocalizedLink
                    key={key}
                    to={key}
                    className={cn(
                      'px-6 py-3 text-lg font-medium transition-colors',
                      isActive
                        ? 'text-gray-900 bg-gray-100 border-l-4 border-gray-900'
                        : 'text-gray-900 hover:bg-gray-50'
                    )}
                  >
                    {t(labelKey)}
                  </LocalizedLink>
                )
              })}
              <div className="border-t border-gray-200 mt-4 pt-4 px-6">
                <LanguageSelector variant="mobile" />
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
